import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { RegistrationUserDataPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/x-tel-input.js';
import '@adornis/popover/x-dropdown-selection.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import {
  IKHInstitutionEnum,
  IKHOccupationEnum,
  isIkhInternalInvoiceReceiverBlocked,
  type IKHStammdaten,
} from '../stammdaten/ikh-stammdaten-plugin.js';
import { IKHInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { InternalInfoBanner } from './helpers.js';

type stammdatenKeys = keyof EntityData<IKHStammdaten>;

export const ikhRegistrationUserDataPlugin: RegistrationUserDataPlugin<IKHStammdaten> = {
  id: 'registration::user-data::ikh',
  type: 'registration::user-data',
  render: (
    stammdatenController: GenericFieldFormController<IKHStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
  ) => {
    const stammdaten = stammdatenController.document;
    const internalInvoiceReceiverBlocked = stammdaten ? isIkhInternalInvoiceReceiverBlocked(stammdaten) : false;
    if (stammdaten?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL && internalInvoiceReceiverBlocked)
      stammdaten.invoiceReceiver = IKHInvoiceReceiverEnum.IKHPRIVATE;

    const genericFieldOption = (fieldName: string) =>
      ({
        placeholder: translationController.translate('ikh', 'field', fieldName as any) as string,
        placeholderMode: 'static-floating',
      }) as const;

    return html`
      <x-flex space="lg">
        <x-flex space="sm">
          <x-tel-input
            ${stammdatenController.field('phoneNumber')}
            placeholder=${translationController.translate('field', 'phone') + '*'}
          ></x-tel-input>

          ${(['salutation', 'title', 'occupation'] as stammdatenKeys[]).map(fieldName =>
            stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
          )}
          ${
            stammdatenController.document?.occupation === IKHOccupationEnum.IKHHESS_HOCHSCHULANGEHOERIGE
              ? html`${stammdatenController.genericField(
                  ['hessHochschulDepartment'],
                  genericFieldOption('hessHochschulDepartment'),
                )}`
              : stammdatenController.document?.occupation === IKHOccupationEnum.IKHTEACHER
                ? nothing
                : html`
                    ${stammdatenController.genericField(['department'], genericFieldOption('department'))}
                    ${
                      stammdatenController.document?.department === IKHInstitutionEnum.IKHEXTERN
                        ? ['externalDepartmentUniversity', 'externalDepartmentDepartment'].map(fieldName =>
                            stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                          )
                        : nothing
                    }
                    ${
                      stammdatenController.document?.department === IKHInstitutionEnum.IKHOTHERS
                        ? ['otherDepartmentFurtherInformation'].map(fieldName =>
                            stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                          )
                        : nothing
                    }
                  `
          }
        </x-flex>
        <x-flex space="md">
          <tr-h4> ${translationController.translate('field', 'address', 'billingAddress')} </tr-h4>
          <x-flex space="sm">
            ${
              stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
                ? InternalInfoBanner()
                : nothing
            }
            <x-dropdown-selection
              .selectables=${[
                ...(internalInvoiceReceiverBlocked ? [] : [IKHInvoiceReceiverEnum.IKHINTERNAL]),
                IKHInvoiceReceiverEnum.IKHEXTERNAL,
                IKHInvoiceReceiverEnum.IKHPRIVATE,
              ]}
              placeholder="${translationController.translate('ikh', 'field', 'invoiceReceiver')}*"
              ${stammdatenController.field('invoiceReceiver')}
              .renderString=${(v?: string) => (v ? translationController.translate('enums', v) : '')}
            ></x-dropdown-selection>
            ${
              stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL
                ? html`${stammdatenController.genericField(['institution'], genericFieldOption('institution'))}
                  ${stammdatenController.genericField(['institutionEmail'], genericFieldOption('institutionEmail'))}`
                : nothing
            }
            ${
              stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
                ? html`
                    ${(['division', 'postbox'] as stammdatenKeys[]).map(fieldName =>
                      stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                    )}
                    <x-input
                      ${stammdatenController.field('costCenter')}
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]+"
                      placeholder=${genericFieldOption('costCenter').placeholder + '*'}
                      placeholder-mode=${genericFieldOption('costCenter').placeholderMode}
                    ></x-input>
                  `
                : nothing
            }
            ${
              stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL
                ? stammdatenController.genericField(['attention'], genericFieldOption('attention'))
                : nothing
            }
            ${
              stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
                ? nothing
                : html`
                    ${stammdatenController.genericField(
                      ['address', 'streetAndHousenumber'],
                      genericFieldOption('streetAndHousenumber'),
                    )}
                    <x-flex horizontal space="sm">
                      ${stammdatenController.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
                      <div style="flex: 1">
                        ${stammdatenController.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}
                      </div>
                    </x-flex>
                  `
            }
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  },
};
